import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
// mui components
import { Avatar, Box, Button, CircularProgress, Snackbar, Stack, Typography } from '@mui/material';
// components
import ArtistBar from './components/artist-bar';
import OnboardingBanner from './components/onboarding-banner';
import StepNav from './components/step-nav';
import StepForm from './components/step-form';
import ReviewStep from './components/review-step';
// custom hooks
import { useUserData } from 'hooks/useUserData';
import { IArtistProfile, useArtistProfiles } from 'hooks/swrRequests/useArtistProfiles';
// api
import api from 'utils/api';
// config
import { getFirstIncompleteStep, getProfileDisplayName, PROFILE_STEPS } from './steps';
// styles
import { fontStyles } from 'styles/font-styles';
import { colors } from 'styles/colors';
import { Trans, useTranslation } from 'react-i18next';

const ProfileSection: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const { userData, updateUserData, onboardingInProgress } = useUserData();
  const { profiles, anyCompleted, isLoading, updateProfileInCache } = useArtistProfiles();

  const [currentId, setCurrentId] = useState<number | null>(null);
  const [stepByProfile, setStepByProfile] = useState<Record<number, number>>({});
  const [completedToastName, setCompletedToastName] = useState<string | null>(null);

  const locked = onboardingInProgress && !anyCompleted;

  // Default selection: first incomplete profile, else the first one.
  useEffect(() => {
    if (currentId === null && profiles?.length) {
      const firstIncomplete = profiles.find((profile) => !profile.completed);
      setCurrentId((firstIncomplete || profiles[0]).id);
    }
  }, [profiles, currentId]);

  const profile = profiles?.find((item) => item.id === currentId);
  const step = profile ? stepByProfile[profile.id] ?? getFirstIncompleteStep(profile) : 0;
  const activeStep = PROFILE_STEPS[step];

  // Unsaved (dirty) step edits, kept across step/artist switches; discarded on leaving the page.
  const draftsRef = useRef<Record<string, Record<string, string>>>({});
  const draftKey = profile ? `${profile.id}-${activeStep.id}` : '';

  const setStep = useCallback(
    (index: number) => {
      if (!profile) return;
      setStepByProfile((prev) => ({ ...prev, [profile.id]: index }));
    },
    [profile]
  );

  const handleContinue = useCallback(
    (updated: IArtistProfile) => {
      updateProfileInCache(updated);
      setStepByProfile((prev) => ({
        ...prev,
        [updated.id]: Math.min((prev[updated.id] ?? step) + 1, PROFILE_STEPS.length - 1),
      }));
    },
    [updateProfileInCache, step]
  );

  const handleCompleted = useCallback(
    (updated: IArtistProfile) => {
      updateProfileInCache(updated);
      setCompletedToastName(getProfileDisplayName(updated) || t('New artist'));
      // refresh onboarding flags so redirects/locks pick up the completion
      api.auth.getProfile().then((data) => updateUserData({ ...data, isAuthorized: true }));
    },
    [updateProfileInCache, updateUserData, t]
  );

  return (
    <Box sx={{ width: '100%', maxWidth: '842px', margin: { xs: '16px', md: '40px' } }}>
      <Typography
        sx={{
          marginBottom: { xs: '16px', md: '32px' },
          ...fontStyles.h3,
          color: colors.text.light.title,
        }}
      >
        {t('Hello')}
        {userData.fullName ? `, ${userData.fullName}` : ''}
      </Typography>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: colors.main.purple }} />
        </Box>
      )}

      {profiles && profiles.length === 0 && (
        <Typography sx={{ fontSize: 16, color: '#6B6B6B' }}>
          <Trans
            t={t}
            i18nKey="No artist profiles. If you want to edit your account details or password, see <a>Account</a>."
            components={{
              a: (
                <Box
                  component={Link}
                  to="/account"
                  sx={{ color: 'inherit', textDecoration: 'underline' }}
                />
              ),
            }}
          />
        </Typography>
      )}

      {profiles && profiles.length > 1 && currentId !== null && (
        <ArtistBar profiles={profiles} currentId={currentId} onSelect={setCurrentId} />
      )}

      {profile && (
        <>
          <OnboardingBanner
            profile={profile}
            artistName={profiles && profiles.length > 1 ? getProfileDisplayName(profile) : null}
          />
          <StepNav current={step} profile={profile} onSelect={setStep} />
          {activeStep.isReview ? (
            <ReviewStep
              profile={profile}
              accountEmail={userData.email}
              locked={locked}
              onJump={setStep}
              onBack={() => setStep(step - 1)}
              onCompleted={handleCompleted}
            />
          ) : (
            <StepForm
              key={draftKey}
              step={activeStep}
              profile={profile}
              accountEmail={userData.email}
              draft={draftsRef.current[draftKey]}
              onDraft={(values) => {
                if (values) draftsRef.current[draftKey] = values;
                else delete draftsRef.current[draftKey];
              }}
              onBack={step > 0 ? () => setStep(step - 1) : null}
              onContinue={handleContinue}
              onProfileChange={updateProfileInCache}
            />
          )}
        </>
      )}

      <Snackbar
        open={!!completedToastName}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => setCompletedToastName(null)}
        sx={{ '& .MuiSnackbarContent-root': { bgcolor: '#0A0A0A', borderRadius: 3 } }}
        message={
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              sx={{ width: 24, height: 24, bgcolor: '#1F9D6B', fontSize: 12, fontWeight: 700 }}
            >
              ✓
            </Avatar>
            <span>
              {completedToastName} {t('profile complete.')}
            </span>
            <Button
              size="small"
              onClick={() => history.push('/deals')}
              sx={{
                bgcolor: colors.main.yellow,
                color: '#141414',
                fontWeight: 700,
                textTransform: 'none',
                px: 1.8,
                '&:hover': { bgcolor: colors.main.yellowDark },
              }}
            >
              {t('Go to Deals')} →
            </Button>
          </Stack>
        }
      />
    </Box>
  );
};

export default ProfileSection;
